import { useRef, useState } from "react";
import {
  useClerk,
  useOrganization,
  useOrganizationList,
} from "@clerk/react";
import { Check, ChevronDown, Plus, Settings } from "lucide-react";
import { useOnClickOutside } from "@/hooks/useOnClickOutside";

type OrgSwitcherProps = {
  collapsed?: boolean;
};

// Custom dropdown over Clerk's org API. Replaces <OrganizationSwitcher>
// because the Clerk component bakes its own text colors that don't
// follow the Gated tokens. Renders nothing when the user belongs to
// fewer than 2 orgs — single-org members have nothing to switch to,
// and ResolveOrgTask auto-activates their sole org.
export function OrgSwitcher({ collapsed = false }: OrgSwitcherProps) {
  const clerk = useClerk();
  const { organization } = useOrganization();
  const { userMemberships, setActive, isLoaded } = useOrganizationList({
    userMemberships: { infinite: true },
  });
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useOnClickOutside(ref, () => setOpen(false));

  const memberships = userMemberships?.data ?? [];
  if (!isLoaded || memberships.length < 2) return null;

  const initial = organization?.name?.[0]?.toUpperCase() ?? "·";

  const onSelect = async (orgId: string) => {
    await setActive?.({ organization: orgId });
    setOpen(false);
    // Hard refresh so TanStack Query caches, zustand stores, and any
    // org-scoped derived state reset against the new org's data.
    window.location.assign("/dashboard");
  };

  return (
    <div ref={ref} className="relative w-full">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="menu"
        aria-expanded={open}
        title={collapsed ? organization?.name ?? "Workspace" : undefined}
        className={
          collapsed
            ? "size-9 mx-auto flex items-center justify-center rounded-md border border-border-1 bg-bg-1 hover:bg-bg-3 transition-colors"
            : "w-full flex items-center justify-between gap-2 px-2.5 py-1.5 rounded-md border border-border-1 bg-bg-1 hover:bg-bg-3 text-small text-fg-1 transition-colors"
        }
      >
        <span className="flex items-center gap-2 min-w-0">
          <span className="size-3.5 rounded-sm bg-accent text-accent-fg flex items-center justify-center text-[9px] font-semibold shrink-0">
            {initial}
          </span>
          {!collapsed ? (
            <span className="truncate">
              {organization?.name ?? "Personal"}
            </span>
          ) : null}
        </span>
        {!collapsed ? (
          <ChevronDown size={11} className="text-fg-3 shrink-0" />
        ) : null}
      </button>

      {open ? (
        <div
          role="menu"
          className="absolute left-0 top-full mt-1.5 z-30 w-56 rounded-md border border-border-1 bg-bg-1 shadow-[var(--shadow-2)] py-1.5"
        >
          <div className="px-3 py-1.5 font-mono text-tiny uppercase tracking-[0.08em] text-fg-3">
            Workspaces
          </div>
          <ul className="flex flex-col">
            {memberships.map((m) => {
              const org = m.organization;
              const isActive = org.id === organization?.id;
              return (
                <li key={org.id}>
                  <button
                    type="button"
                    role="menuitemradio"
                    aria-checked={isActive}
                    onClick={() => void onSelect(org.id)}
                    className="w-full flex items-center gap-2 px-3 py-1.5 text-small text-fg-1 hover:bg-bg-2 text-left"
                  >
                    <span className="size-3.5 rounded-sm bg-accent text-accent-fg flex items-center justify-center text-[9px] font-semibold shrink-0">
                      {org.name[0]?.toUpperCase()}
                    </span>
                    <span className="flex-1 truncate">{org.name}</span>
                    {isActive ? (
                      <Check size={12} className="text-accent shrink-0" />
                    ) : null}
                  </button>
                </li>
              );
            })}
          </ul>
          <div className="my-1 h-px bg-border-1" />
          <button
            type="button"
            role="menuitem"
            onClick={() => {
              setOpen(false);
              clerk.openOrganizationProfile();
            }}
            className="w-full flex items-center gap-2 px-3 py-1.5 text-small text-fg-2 hover:bg-bg-2 hover:text-fg-1 text-left"
          >
            <Settings size={13} strokeWidth={1.5} />
            Manage organization
          </button>
          <button
            type="button"
            role="menuitem"
            onClick={() => {
              setOpen(false);
              clerk.openCreateOrganization();
            }}
            className="w-full flex items-center gap-2 px-3 py-1.5 text-small text-fg-2 hover:bg-bg-2 hover:text-fg-1 text-left"
          >
            <Plus size={13} strokeWidth={1.5} />
            New organization
          </button>
        </div>
      ) : null}
    </div>
  );
}
